import { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Plus,
  Settings,
  Trash2,
  CheckCircle,
  Briefcase,
  Zap,
  Sparkles,
  Rocket,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import { UPDATE_BLAST_TIER, UPDATE_BLAST_FEATURES } from '@/graphql/organizations';
import {
  GET_PSP_ASSIGNMENTS_FOR_ORG,
  GET_PSP_ASSIGNMENTS_FOR_PSP,
  GET_AVAILABLE_PSPS_FOR_ORG,
  GET_AVAILABLE_CLIENTS_FOR_PSP,
  ASSIGN_PSP_TO_CLIENT,
  TERMINATE_PSP_ASSIGNMENT,
  type GetPspAssignmentsForOrgData,
  type GetPspAssignmentsForOrgVars,
  type GetPspAssignmentsForPspData,
  type GetPspAssignmentsForPspVars,
  type GetAvailablePspsForOrgData,
  type GetAvailablePspsForOrgVars,
  type GetAvailableClientsForPspData,
  type GetAvailableClientsForPspVars,
} from '@/graphql/pspAssignments';
import { InstitutionAdminsCard } from './blast/InstitutionAdminsCard';
import { PspAdminsCard } from './blast/PspAdminsCard';
import { IpWhitelistCard } from './blast/IpWhitelistCard';
import { SftpAccessCard } from './blast/SftpAccessCard';

// BlastTier types (matches the GraphQL enum)
type BlastTier = 'zero' | 'lite' | 'full';
type GraphQLBlastTier = 'ZERO' | 'LITE' | 'FULL';

// Helper to check if tier allows multiple PSPs
const tierAllowsMultiplePsps = (tier: BlastTier): boolean => tier === 'full';

/**
 * The v1 BLAST feature entitlement contract — exactly these seven toggles,
 * persisted to blast.organizations.features via updateBlastFeatures. Slope
 * is the entitlement system of record; BLAST enforces fail-closed and picks
 * changes up at next login / within its ~15-minute cache.
 *
 * Not toggles (always-on core in BLAST): Loan Applications (incl. the
 * Decision tab), Entity Management, Loan Management, Reports, Forms.
 * Deferred: Sales, Loan Servicing (returns when /loan-servicing ships).
 */
interface BlastFeaturesState {
  cms: boolean;
  docPrep: boolean;
  sba: boolean;
  docManagement: boolean;
  tasksWorkflows: boolean;
  portfolioMonitoring: boolean;
  borrowerPortal: boolean;
}

const BLAST_FEATURE_TOGGLES: Array<{
  key: keyof BlastFeaturesState;
  label: string;
  desc: string;
}> = [
  {
    key: 'cms',
    label: 'Credit Management System',
    desc: 'Spreads (incl. financial periods & analysis), Covenants, and Memo',
  },
  { key: 'docPrep', label: 'Doc Prep', desc: 'Document preparation and generation' },
  { key: 'sba', label: 'SBA', desc: 'SBA tab, loan flagging, and E-Tran submission' },
  {
    key: 'docManagement',
    label: 'Document Management',
    desc: 'Ticklers, schedules, and document exception tracking',
  },
  {
    key: 'tasksWorkflows',
    label: 'Tasks & Workflows',
    desc: 'Task management plus workflow authoring and assignment',
  },
  {
    key: 'portfolioMonitoring',
    label: 'Portfolio Monitoring',
    desc: 'Loan portfolio tracking and analytics',
  },
  {
    key: 'borrowerPortal',
    label: 'Borrower Portal',
    desc: 'Customer-facing loan application portal',
  },
];

interface BlastTabProps {
  clientId: string;
  clientType: 'CFI' | 'PSP';
  blastTier: GraphQLBlastTier;
  blastFeatures: BlastFeaturesState;
  onTierUpdate?: () => void;
}

// Helper to convert GraphQL tier to local tier
const graphQLToLocalTier = (tier: GraphQLBlastTier): BlastTier => tier.toLowerCase() as BlastTier;
const localToGraphQLTier = (tier: BlastTier): GraphQLBlastTier => tier.toUpperCase() as GraphQLBlastTier;

export function BlastTab({ clientId, clientType, blastTier, blastFeatures, onTierUpdate }: BlastTabProps) {
  const isPsp = clientType === 'PSP';
  // BLAST Tier and Features state - initialize from props (persisted state)
  const [selectedTier, setSelectedTier] = useState<BlastTier>(graphQLToLocalTier(blastTier));
  const [features, setFeatures] = useState<BlastFeaturesState>(blastFeatures);
  const [tierUpdateError, setTierUpdateError] = useState<string | null>(null);
  const [featureUpdateError, setFeatureUpdateError] = useState<string | null>(null);
  const [updatingFeature, setUpdatingFeature] = useState<keyof BlastFeaturesState | null>(null);

  // GraphQL mutation for updating BLAST tier
  const [updateBlastTier, { loading: isUpdatingTier }] = useMutation(UPDATE_BLAST_TIER, {
    onCompleted: () => {
      onTierUpdate?.();
      setTierUpdateError(null);
    },
    onError: (error) => {
      console.error('Failed to update BLAST tier:', error);
      setTierUpdateError(error.message || 'Failed to update tier');
      // Revert to original tier on error
      setSelectedTier(graphQLToLocalTier(blastTier));
    },
  });

  // GraphQL mutation for updating BLAST feature entitlements. Optimistic
  // local toggle; on error revert to the last persisted state from props.
  const [updateBlastFeatures] = useMutation(UPDATE_BLAST_FEATURES, {
    onCompleted: () => {
      setFeatureUpdateError(null);
      setUpdatingFeature(null);
      onTierUpdate?.(); // refetch so props reflect the persisted state
    },
    onError: (error) => {
      console.error('Failed to update BLAST features:', error);
      setFeatureUpdateError(error.message || 'Failed to update features');
      setUpdatingFeature(null);
      setFeatures(blastFeatures);
    },
  });

  // Sync state when props change
  useEffect(() => {
    setSelectedTier(graphQLToLocalTier(blastTier));
  }, [blastTier]);
  useEffect(() => {
    setFeatures(blastFeatures);
  }, [blastFeatures]);

  // ==================== PSP ASSIGNMENT QUERIES ====================
  // CFI view: PSPs assigned to this org
  const { data: orgAssignmentsData } = useQuery<GetPspAssignmentsForOrgData, GetPspAssignmentsForOrgVars>(
    GET_PSP_ASSIGNMENTS_FOR_ORG,
    { variables: { orgId: clientId }, skip: isPsp }
  );
  // PSP view: clients assigned to this PSP
  const { data: pspAssignmentsData } = useQuery<GetPspAssignmentsForPspData, GetPspAssignmentsForPspVars>(
    GET_PSP_ASSIGNMENTS_FOR_PSP,
    { variables: { pspOrgId: clientId }, skip: !isPsp }
  );
  // CFI view: available PSPs for dropdown
  const { data: availablePspsData } = useQuery<GetAvailablePspsForOrgData, GetAvailablePspsForOrgVars>(
    GET_AVAILABLE_PSPS_FOR_ORG,
    { variables: { orgId: clientId }, skip: isPsp }
  );
  // PSP view: available clients for dropdown
  const { data: availableClientsData } = useQuery<GetAvailableClientsForPspData, GetAvailableClientsForPspVars>(
    GET_AVAILABLE_CLIENTS_FOR_PSP,
    { variables: { pspOrgId: clientId }, skip: !isPsp }
  );

  // Derived data from queries
  const assignedPsps = orgAssignmentsData?.pspAssignmentsForOrg ?? [];
  const assignedClients = pspAssignmentsData?.pspAssignmentsForPsp ?? [];
  const availablePspsForAssignment = availablePspsData?.availablePspsForOrg ?? [];
  const availableClientsForAssignment = availableClientsData?.availableClientsForPsp ?? [];

  // ==================== PSP ASSIGNMENT MUTATIONS ====================
  const [assignPspToClient] = useMutation(ASSIGN_PSP_TO_CLIENT, {
    refetchQueries: [
      { query: GET_PSP_ASSIGNMENTS_FOR_ORG, variables: { orgId: clientId } },
      { query: GET_AVAILABLE_PSPS_FOR_ORG, variables: { orgId: clientId } },
    ],
  });
  const [terminatePspAssignment] = useMutation(TERMINATE_PSP_ASSIGNMENT, {
    refetchQueries: [
      { query: GET_PSP_ASSIGNMENTS_FOR_ORG, variables: { orgId: clientId } },
      { query: GET_PSP_ASSIGNMENTS_FOR_PSP, variables: { pspOrgId: clientId } },
      { query: GET_AVAILABLE_PSPS_FOR_ORG, variables: { orgId: clientId } },
      { query: GET_AVAILABLE_CLIENTS_FOR_PSP, variables: { pspOrgId: clientId } },
    ],
  });

  // Tier change confirmation state
  const [pendingTierChange, setPendingTierChange] = useState<BlastTier | null>(null);
  const [tierChangeConfirmText, setTierChangeConfirmText] = useState('');

  // Modal states
  const [showAssignPspModal, setShowAssignPspModal] = useState(false);
  const [showDeletePspConfirm, setShowDeletePspConfirm] = useState<string | null>(null);
  const [showAssignClientModal, setShowAssignClientModal] = useState(false);
  const [showDeleteClientConfirm, setShowDeleteClientConfirm] = useState<string | null>(null);

  // PSP form state
  const [selectedPspId, setSelectedPspId] = useState('');

  // Client form state (for PSP's "Assigned Clients")
  const [selectedClientId, setSelectedClientId] = useState('');

  // Get tier display name
  const getTierDisplayName = (tier: BlastTier): string => {
    switch (tier) {
      case 'zero': return 'BLAST Zero';
      case 'lite': return 'BLAST Lite';
      case 'full': return 'BLAST';
    }
  };

  // Initiate tier change - shows confirmation modal
  const initiateTierChange = (tier: BlastTier) => {
    if (tier === selectedTier) return; // No change needed
    setPendingTierChange(tier);
    setTierChangeConfirmText('');
  };

  // Cancel tier change
  const cancelTierChange = () => {
    setPendingTierChange(null);
    setTierChangeConfirmText('');
  };

  // Confirm and execute tier change
  const confirmTierChange = async () => {
    if (!pendingTierChange || tierChangeConfirmText.toUpperCase() !== 'CONFIRM') return;
    
    // Optimistically update the UI. Feature entitlements are persisted
    // contract state and deliberately NOT touched by a tier change.
    setSelectedTier(pendingTierChange);

    setPendingTierChange(null);
    setTierChangeConfirmText('');

    // Call the mutation to persist the change
    await updateBlastTier({
      variables: {
        organizationId: clientId,
        input: {
          blastTier: localToGraphQLTier(pendingTierChange),
        },
      },
    });
  };

  // Check if confirm button should be enabled
  const isConfirmEnabled = tierChangeConfirmText.toUpperCase() === 'CONFIRM';

  // Toggle a feature entitlement: optimistic local flip + persisted write to
  // BLAST. PSP orgs are always all-true (BLAST's acting-org rule) — their
  // toggles render read-only and this handler refuses to fire.
  const toggleFeature = (feature: keyof BlastFeaturesState) => {
    if (isPsp || updatingFeature) return;
    const newValue = !features[feature];
    setFeatures({ ...features, [feature]: newValue });
    setUpdatingFeature(feature);
    void updateBlastFeatures({
      variables: {
        organizationId: clientId,
        input: { [feature]: newValue },
      },
    });
  };

  // PSP handlers
  const resetPspForm = () => {
    setSelectedPspId('');
  };

  const openAssignPspModal = () => {
    resetPspForm();
    setShowAssignPspModal(true);
  };

  const handleAssignPsp = async () => {
    if (!selectedPspId) return;
    try {
      await assignPspToClient({
        variables: { pspId: selectedPspId, orgId: clientId },
      });
      setShowAssignPspModal(false);
      resetPspForm();
    } catch (err) {
      console.error('Failed to assign PSP:', err);
    }
  };

  const handleDeletePsp = async (assignmentId: string) => {
    try {
      await terminatePspAssignment({ variables: { assignmentId } });
      setShowDeletePspConfirm(null);
    } catch (err) {
      console.error('Failed to terminate PSP assignment:', err);
    }
  };

  // Check if PSP limit is reached for Zero/Lite tiers
  const isPspLimitReached = !tierAllowsMultiplePsps(selectedTier) && assignedPsps.length >= 1;
  
  // Check if PSP operations are disabled due to tier restrictions
  const canAddMorePsps = tierAllowsMultiplePsps(selectedTier) || assignedPsps.length === 0;

  // Client handlers (for PSP's "Assigned Clients" — inverse of Assigned PSPs)
  const resetClientForm = () => {
    setSelectedClientId('');
  };

  const openAssignClientModal = () => {
    resetClientForm();
    setShowAssignClientModal(true);
  };

  const handleAssignClient = async () => {
    if (!selectedClientId) return;
    try {
      await assignPspToClient({
        variables: { pspId: clientId, orgId: selectedClientId },
      });
      setShowAssignClientModal(false);
      resetClientForm();
    } catch (err) {
      console.error('Failed to assign client:', err);
    }
  };

  const handleDeleteClient = async (assignmentId: string) => {
    try {
      await terminatePspAssignment({ variables: { assignmentId } });
      setShowDeleteClientConfirm(null);
    } catch (err) {
      console.error('Failed to terminate client assignment:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* BLAST Configuration Card */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-pond dark:text-pond-light" />
            <h2 className="text-lg font-semibold text-slate dark:text-mist">BLAST Configuration</h2>
          </div>
          <p className="text-sm text-chalkboard dark:text-rain-cloud mt-1">
            {isPsp
              ? 'PSPs always receive BLAST Full. Configure features below.'
              : 'Select the BLAST tier and configure features for this institution'}
          </p>
        </div>
        <div className="card-body space-y-6">
          {/* Tier Selection — CFI only; PSPs always get BLAST Full */}
          {isPsp ? (
            <div className="relative p-4 rounded-lg border-2 border-turquoise dark:border-aqua bg-turquoise/5 dark:bg-aqua/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-turquoise/20 dark:bg-aqua/20">
                  <Rocket className="w-5 h-5 text-turquoise dark:text-aqua" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate dark:text-mist">BLAST Full</h3>
                  <p className="text-sm text-chalkboard dark:text-rain-cloud">
                    All PSPs receive the full BLAST platform with all features available.
                  </p>
                </div>
                <div className="ml-auto">
                  <CheckCircle className="w-5 h-5 text-turquoise dark:text-aqua" />
                </div>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-slate dark:text-mist mb-3">
                BLAST Tier
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* BLAST Zero */}
                <button
                  onClick={() => initiateTierChange('zero')}
                  className={cn(
                    'relative p-4 rounded-lg border-2 text-left transition-all',
                    selectedTier === 'zero'
                      ? 'border-pond dark:border-pond-light bg-pond/5 dark:bg-pond-light/5'
                      : 'border-pale-gray-3 dark:border-night-pond hover:border-pond/50 dark:hover:border-pond-light/50'
                  )}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center',
                      selectedTier === 'zero' ? 'bg-pond/20 dark:bg-pond-light/20' : 'bg-pale-gray-2 dark:bg-night-pond'
                    )}>
                      <Zap className={cn(
                        'w-5 h-5',
                        selectedTier === 'zero' ? 'text-pond dark:text-pond-light' : 'text-chalkboard dark:text-rain-cloud'
                      )} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate dark:text-mist">BLAST Zero</h3>
                      <p className="text-xs text-chalkboard dark:text-rain-cloud">Basic access</p>
                    </div>
                  </div>
                  <p className="text-sm text-chalkboard dark:text-rain-cloud">
                    No additional features included
                  </p>
                  {selectedTier === 'zero' && (
                    <div className="absolute top-3 right-3">
                      <CheckCircle className="w-5 h-5 text-pond dark:text-pond-light" />
                    </div>
                  )}
                </button>

                {/* BLAST Lite */}
                <button
                  onClick={() => initiateTierChange('lite')}
                  className={cn(
                    'relative p-4 rounded-lg border-2 text-left transition-all',
                    selectedTier === 'lite'
                      ? 'border-violet dark:border-violet-light bg-violet/5 dark:bg-violet-light/5'
                      : 'border-pale-gray-3 dark:border-night-pond hover:border-violet/50 dark:hover:border-violet-light/50'
                  )}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center',
                      selectedTier === 'lite' ? 'bg-violet/20 dark:bg-violet-light/20' : 'bg-pale-gray-2 dark:bg-night-pond'
                    )}>
                      <Sparkles className={cn(
                        'w-5 h-5',
                        selectedTier === 'lite' ? 'text-violet dark:text-violet-light' : 'text-chalkboard dark:text-rain-cloud'
                      )} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate dark:text-mist">BLAST Lite</h3>
                      <p className="text-xs text-chalkboard dark:text-rain-cloud">Essential features</p>
                    </div>
                  </div>
                  <p className="text-sm text-chalkboard dark:text-rain-cloud">
                    Optional Borrower Portal access
                  </p>
                  {selectedTier === 'lite' && (
                    <div className="absolute top-3 right-3">
                      <CheckCircle className="w-5 h-5 text-violet dark:text-violet-light" />
                    </div>
                  )}
                </button>

                {/* BLAST Full */}
                <button
                  onClick={() => initiateTierChange('full')}
                  className={cn(
                    'relative p-4 rounded-lg border-2 text-left transition-all',
                    selectedTier === 'full'
                      ? 'border-turquoise dark:border-aqua bg-turquoise/5 dark:bg-aqua/5'
                      : 'border-pale-gray-3 dark:border-night-pond hover:border-turquoise/50 dark:hover:border-aqua/50'
                  )}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <div className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center',
                      selectedTier === 'full' ? 'bg-turquoise/20 dark:bg-aqua/20' : 'bg-pale-gray-2 dark:bg-night-pond'
                    )}>
                      <Rocket className={cn(
                        'w-5 h-5',
                        selectedTier === 'full' ? 'text-turquoise dark:text-aqua' : 'text-chalkboard dark:text-rain-cloud'
                      )} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate dark:text-mist">BLAST</h3>
                      <p className="text-xs text-chalkboard dark:text-rain-cloud">Full platform</p>
                    </div>
                  </div>
                  <p className="text-sm text-chalkboard dark:text-rain-cloud">
                    All features available
                  </p>
                  {selectedTier === 'full' && (
                    <div className="absolute top-3 right-3">
                      <CheckCircle className="w-5 h-5 text-turquoise dark:text-aqua" />
                    </div>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* BLAST Feature Entitlements — the 7-key contract, persisted to
              blast.organizations.features. Orthogonal to tier. */}
          <div className="border-t border-pale-gray-3 dark:border-night-pond pt-6">
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              BLAST Features
            </label>
            <p className="text-sm text-chalkboard dark:text-rain-cloud mb-3">
              {isPsp
                ? 'PSP organizations always hold every feature so their users can service restricted clients — toggles are read-only.'
                : 'Changes apply in BLAST at next login (within ~15 minutes for active sessions). Loan Applications, Entity Management, Loan Management, Reports, and Forms are always included.'}
            </p>
            {featureUpdateError && (
              <div className="p-3 mb-3 bg-rust/10 border border-rust/20 rounded-lg">
                <p className="text-sm text-rust dark:text-salmon">{featureUpdateError}</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {BLAST_FEATURE_TOGGLES.map((feature) => {
                const enabled = isPsp ? true : features[feature.key];
                return (
                  <div key={feature.key} className="flex items-center justify-between p-3 bg-pale-gray-2 dark:bg-night-pond rounded-lg">
                    <div className="flex-1 min-w-0 mr-3">
                      <p className="font-medium text-slate dark:text-mist">{feature.label}</p>
                      <p className="text-sm text-chalkboard dark:text-rain-cloud truncate">{feature.desc}</p>
                    </div>
                    {updatingFeature === feature.key ? (
                      <Loader2 className="w-5 h-5 flex-shrink-0 animate-spin text-turquoise dark:text-aqua" />
                    ) : (
                      <button
                        onClick={() => toggleFeature(feature.key)}
                        disabled={isPsp || updatingFeature !== null}
                        title={isPsp ? 'PSP organizations always hold every feature' : undefined}
                        className={cn(
                          'relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors',
                          enabled ? 'bg-turquoise dark:bg-aqua' : 'bg-chalkboard/30 dark:bg-rain-cloud/30',
                          isPsp && 'opacity-60 cursor-not-allowed'
                        )}
                      >
                        <span
                          className={cn(
                            'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                            enabled ? 'translate-x-6' : 'translate-x-1'
                          )}
                        />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Administrators Card — Institution Admins for CFI, PSP Admins for PSP */}
      {isPsp ? (
        <PspAdminsCard clientId={clientId} />
      ) : (
        <InstitutionAdminsCard clientId={clientId} />
      )}

      {/* Assigned PSPs (CFI) or Assigned Clients (PSP) */}
      {isPsp ? (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-orange dark:text-orange-light" />
                  <h2 className="text-lg font-semibold text-slate dark:text-mist">Assigned Clients</h2>
                </div>
                <p className="text-sm text-chalkboard dark:text-rain-cloud mt-1">
                  Institutions (CFIs) that this PSP is assigned to service
                </p>
              </div>
              <button
                onClick={openAssignClientModal}
                className="btn-primary"
                disabled={availableClientsForAssignment.length === 0}
              >
                <Plus className="w-4 h-4" />
                Assign Client
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            <table className="table">
              <thead>
                <tr>
                  <th>Client Name</th>
                  <th>Contact</th>
                  <th>Date Assigned</th>
                  <th className="w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {assignedClients.map((client) => (
                  <tr key={client.id} className="group">
                    <td className="font-medium text-slate dark:text-mist">{client.orgName}</td>
                    <td className="text-chalkboard dark:text-rain-cloud">—</td>
                    <td className="text-chalkboard dark:text-rain-cloud">
                      {new Date(client.assignedAt).toLocaleDateString()}
                    </td>
                    <td>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setShowDeleteClientConfirm(client.id)}
                          className="btn-ghost btn-icon btn-sm text-rust dark:text-salmon"
                          title="Remove Client"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {assignedClients.length === 0 && (
              <div className="p-8 text-center text-chalkboard dark:text-rain-cloud">
                No clients assigned yet. Click &quot;Assign Client&quot; to get started.
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-orange dark:text-orange-light" />
                  <h2 className="text-lg font-semibold text-slate dark:text-mist">Assigned PSPs</h2>
                </div>
                <p className="text-sm text-chalkboard dark:text-rain-cloud mt-1">
                  Professional Service Providers assigned to this institution
                  {!tierAllowsMultiplePsps(selectedTier) && (
                    <span className="ml-2 text-orange dark:text-orange-light font-medium">
                      (Limited to 1 PSP for {selectedTier === 'zero' ? 'BLAST Zero' : 'BLAST Lite'})
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={openAssignPspModal}
                className="btn-primary"
                disabled={!canAddMorePsps || availablePspsForAssignment.length === 0}
                title={isPspLimitReached ? `${selectedTier === 'zero' ? 'BLAST Zero' : 'BLAST Lite'} is limited to 1 PSP` : undefined}
              >
                <Plus className="w-4 h-4" />
                Assign PSP
              </button>
            </div>
          </div>
          <div className="card-body p-0">
            <table className="table">
              <thead>
                <tr>
                  <th>PSP Name</th>
                  <th>Contact</th>
                  <th>Date Assigned</th>
                  <th className="w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                {assignedPsps.map((psp) => (
                  <tr key={psp.id} className="group">
                    <td className="font-medium text-slate dark:text-mist">{psp.pspName}</td>
                    <td className="text-chalkboard dark:text-rain-cloud">—</td>
                    <td className="text-chalkboard dark:text-rain-cloud">
                      {new Date(psp.assignedAt).toLocaleDateString()}
                    </td>
                    <td>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => setShowDeletePspConfirm(psp.id)}
                          className="btn-ghost btn-icon btn-sm text-rust dark:text-salmon"
                          title="Remove PSP"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {assignedPsps.length === 0 && (
              <div className="p-8 text-center text-chalkboard dark:text-rain-cloud">
                No PSPs assigned yet. Click &quot;Assign PSP&quot; to get started.
              </div>
            )}
          </div>
        </div>
      )}

      {/* IP Whitelist Card */}
      <IpWhitelistCard clientId={clientId} />

      {/* SFTP Access Card */}
      <SftpAccessCard clientId={clientId} />

      {/* Assign PSP Modal */}
      <Modal
        isOpen={showAssignPspModal}
        onClose={() => { setShowAssignPspModal(false); resetPspForm(); }}
        title="Assign PSP"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Select PSP
            </label>
            <select
              value={selectedPspId}
              onChange={(e) => setSelectedPspId(e.target.value)}
              className="select"
            >
              <option value="">Choose a PSP...</option>
              {availablePspsForAssignment.map((psp) => (
                <option key={psp.id} value={psp.id}>
                  {psp.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => { setShowAssignPspModal(false); resetPspForm(); }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleAssignPsp}
              className="btn-primary"
              disabled={!selectedPspId}
            >
              Assign PSP
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete PSP Confirmation Modal */}
      <Modal
        isOpen={!!showDeletePspConfirm}
        onClose={() => setShowDeletePspConfirm(null)}
        title="Remove PSP Assignment"
      >
        <div className="space-y-4">
          <p className="text-slate dark:text-mist">
            Are you sure you want to remove this PSP assignment? This will not delete the PSP, only remove their assignment to this client.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowDeletePspConfirm(null)} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={() => showDeletePspConfirm && handleDeletePsp(showDeletePspConfirm)}
              className="btn-primary bg-rust hover:bg-rust/90"
            >
              <Trash2 className="w-4 h-4" />
              Remove
            </button>
          </div>
        </div>
      </Modal>

      {/* Assign Client Modal (PSP view) */}
      <Modal
        isOpen={showAssignClientModal}
        onClose={() => { setShowAssignClientModal(false); resetClientForm(); }}
        title="Assign Client"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-1">
              Select Client
            </label>
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              className="select"
            >
              <option value="">Choose a client...</option>
              {availableClientsForAssignment.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => { setShowAssignClientModal(false); resetClientForm(); }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleAssignClient}
              className="btn-primary"
              disabled={!selectedClientId}
            >
              Assign Client
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Client Assignment Confirmation Modal (PSP view) */}
      <Modal
        isOpen={!!showDeleteClientConfirm}
        onClose={() => setShowDeleteClientConfirm(null)}
        title="Remove Client Assignment"
      >
        <div className="space-y-4">
          <p className="text-slate dark:text-mist">
            Are you sure you want to remove this client assignment? This will not delete the client, only remove their assignment to this PSP.
          </p>
          <div className="flex justify-end gap-3">
            <button onClick={() => setShowDeleteClientConfirm(null)} className="btn-secondary">
              Cancel
            </button>
            <button
              onClick={() => showDeleteClientConfirm && handleDeleteClient(showDeleteClientConfirm)}
              className="btn-primary bg-rust hover:bg-rust/90"
            >
              <Trash2 className="w-4 h-4" />
              Remove
            </button>
          </div>
        </div>
      </Modal>

      {/* Tier Change Confirmation Modal */}
      <Modal
        isOpen={!!pendingTierChange}
        onClose={cancelTierChange}
        title="Confirm Tier Change"
      >
        <div className="space-y-4">
          {/* Warning Banner */}
          <div className="p-4 bg-rust/10 dark:bg-salmon/10 rounded-lg border border-rust/30 dark:border-salmon/30">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-rust dark:text-salmon flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-rust dark:text-salmon mb-1">
                  Warning: This changes the client&apos;s BLAST tier
                </h4>
                <p className="text-sm text-slate dark:text-mist">
                  You are about to change the BLAST tier from{' '}
                  <strong>{getTierDisplayName(selectedTier)}</strong> to{' '}
                  <strong>{pendingTierChange ? getTierDisplayName(pendingTierChange) : ''}</strong>.
                </p>
                <p className="text-sm text-chalkboard dark:text-rain-cloud mt-2">
                  Feature entitlements are not affected by tier changes — they are
                  managed separately in the BLAST Features section.
                </p>
              </div>
            </div>
          </div>

          {/* Confirmation Input */}
          <div>
            <label className="block text-sm font-medium text-slate dark:text-mist mb-2">
              To confirm, type <strong className="font-mono bg-pale-gray-2 dark:bg-night-pond px-2 py-0.5 rounded">CONFIRM</strong> below:
            </label>
            <input
              type="text"
              value={tierChangeConfirmText}
              onChange={(e) => setTierChangeConfirmText(e.target.value)}
              placeholder="Type CONFIRM to proceed"
              className={cn(
                'input font-mono',
                tierChangeConfirmText.length > 0 && !isConfirmEnabled && 'border-rust dark:border-salmon'
              )}
              autoFocus
            />
            {tierChangeConfirmText.length > 0 && !isConfirmEnabled && (
              <p className="text-sm text-rust dark:text-salmon mt-1">
                Please type &quot;CONFIRM&quot; exactly to enable the button
              </p>
            )}
          </div>

          {/* Error Message */}
          {tierUpdateError && (
            <div className="p-3 bg-rust/10 border border-rust/20 rounded-lg">
              <p className="text-sm text-rust dark:text-salmon">{tierUpdateError}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <button onClick={cancelTierChange} className="btn-secondary" disabled={isUpdatingTier}>
              Cancel
            </button>
            <button
              onClick={confirmTierChange}
              className={cn(
                'btn-primary',
                isConfirmEnabled && !isUpdatingTier ? 'bg-rust hover:bg-rust/90' : 'opacity-50 cursor-not-allowed'
              )}
              disabled={!isConfirmEnabled || isUpdatingTier}
            >
              {isUpdatingTier ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <AlertTriangle className="w-4 h-4" />
                  Change Tier
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
